import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, colors, InputText } from '@insurancemenu/imenu-components-lib';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import FormModal from 'components/common/modals/FormModal';
import IProposalPageStore from 'store/modules/proposal/iProposalPageStore';
import CreatePortfolioModalStore, { PortfolioPresenter } from 'store/modules/proposal/ui/createPortfolioModalStore';
import { useRFQProposalPageType } from 'global/hooks/useRFQProposalPageType';
import { CombinationType } from 'types/cartServiceV2';
import { createStyles } from './styles';

interface ModalProps<PageStore extends IProposalPageStore> {
  productLineId: ProductLineId;
  createPortfolioCombinationType: CombinationType;
  store: CreatePortfolioModalStore<PageStore>;
}

interface ProductsSectionProps {
  availableToAddItems: string[];
  inPortfolioItems: string[];
  maxProductsNumber: number;
  formatPortfolios: (data: string[]) => PortfolioPresenter[];
  addPlan: (planId: string) => Promise<void>;
}

const AvailableProductsSection = observer(
  ({
    availableToAddItems,
    inPortfolioItems,
    maxProductsNumber,
    formatPortfolios,
    addPlan,
  }: ProductsSectionProps): JSX.Element => {
    const classes = createStyles();

    if (inPortfolioItems.length < 2 && availableToAddItems.length < 1) {
      return <div className={classes.maxProductsReachedMsg}>No products available to combine with.</div>;
    }

    if (maxProductsNumber !== 0 && inPortfolioItems.length >= maxProductsNumber) {
      return (
        <div className={classes.maxProductsReachedMsg}>
          You&apos;ve exceeded the max number of plans in a portfolio allowed.
        </div>
      );
    }

    return (
      <>
        {formatPortfolios(availableToAddItems).map(({ id, name }, index) => (
          <li key={id} className={classes.productRecord}>
            <span>{`${index + 1}. ${name}`}</span>
            <Button
              type="text"
              color="primary"
              className={classes.actionBtn}
              onClick={async (): Promise<void> => addPlan(id)}
            >
              Add
            </Button>
          </li>
        ))}
      </>
    );
  },
);

function CreatePortfolioModal<PageStore extends IProposalPageStore>({
  productLineId,
  createPortfolioCombinationType,
  store,
}: ModalProps<PageStore>): JSX.Element {
  const {
    isLoading,
    getInitialPortfolioMap,
    portfolioName,
    portfolioNameError,
    setPortfolioName,
    closeModal,
    modalIsVisible,
    inPortfolioItems,
    availableToAddItems,
    removePlan,
    addPlan,
    maxProductsNumber,
    formatPortfolios,
    createPortfolio,
    isDataValid,
    setCreatePortfolioCombinationType,
  } = store;

  const pageType = useRFQProposalPageType();

  useEffect(() => {
    getInitialPortfolioMap();
    setCreatePortfolioCombinationType(createPortfolioCombinationType);
  }, [getInitialPortfolioMap]);

  const productLineName = PRODUCT_LINES_CONTENT_MAP[productLineId].label;

  const onCreatePortfolio = async (): Promise<void> => {
    await createPortfolio(pageType);
  };

  const classes = createStyles();
  return (
    <FormModal
      show={modalIsVisible}
      onClose={closeModal}
      id="create-portfolio"
      title={`Create ${productLineName} Portfolio`}
      subtitle="Enter a name for the portfolio you have built"
      submitButton={
        <div className={classes.submitButtonWrapper}>
          <Button color="primary" onClick={onCreatePortfolio} size="large" disabled={isLoading || !isDataValid()}>
            Create Portfolio
          </Button>
          {inPortfolioItems.length === 1 && (
            <p className={classes.errorDisclaimer}>
              Minimum 2 products required
              <br /> to create portfolio
            </p>
          )}
        </div>
      }
      loading={isLoading}
      formContent={
        <div className={classes.wrapper}>
          <InputText
            label="Portfolio Name"
            name="portfolioName"
            value={portfolioName}
            onChange={setPortfolioName}
            validationPassed={!portfolioNameError}
            validationMessage={portfolioNameError || undefined}
            maxLength={50}
            className={classes.portfolioInput}
          />
          <div className={classes.productsWrapper}>
            <div className={classes.productsTitleWrapper}>
              <h5 className={classes.productsTitle}>In Portfolio:</h5>
              {maxProductsNumber > 0 && (
                <span className={classes.productsSubtitle}>
                  Up to <b>{maxProductsNumber}</b> Products Allowed
                </span>
              )}
            </div>
            <ul className={classes.productsList}>
              {formatPortfolios(inPortfolioItems).map(({ id, name }, index) => (
                <li key={id} className={classes.productRecord}>
                  <span>{`${index + 1}. ${name}`}</span>
                  <Button
                    type="text"
                    color="custom"
                    customColor={colors.red[500]}
                    className={classes.actionBtn}
                    onClick={(): void => removePlan(id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
          <div className={classes.productsWrapper}>
            <div className={classes.productsTitleWrapper}>
              <h5 className={classes.productsTitle}>Available for adding:</h5>
            </div>
            <ul className={classes.productsList} id="available-agents-list">
              <AvailableProductsSection
                availableToAddItems={availableToAddItems}
                inPortfolioItems={inPortfolioItems}
                maxProductsNumber={maxProductsNumber}
                formatPortfolios={formatPortfolios}
                addPlan={addPlan}
              />
            </ul>
          </div>
        </div>
      }
      closeButtonText="Cancel"
    />
  );
}

export default observer(CreatePortfolioModal);
